import type { CalculatorContent, Faq } from '@/types/calculator'
import { rebates2027 } from '@/data/tax/2027'
import type { SalaryIncreaseInputs } from './types'

export const content: CalculatorContent<SalaryIncreaseInputs> = {
  intro:
    'This salary increase calculator shows what a raise or bonus means for your gross pay in South Africa — and, optionally, an estimate of your net pay after the increase once marginal tax is taken into account.',
  howItWorks: [
    'Enter your current salary (monthly or annual), the percentage increase on offer, and an optional bonus — either a Rand amount or a percentage of salary.',
    'The calculator annualises your salary, applies the increase and adds the bonus to give your new gross figures. Gross and monthly differences are shown immediately.',
    'If you enable the tax estimate, the calculator works out annual tax on the income figure you provide, then on that income plus the increase and bonus, using the SARS tax brackets and the primary rebate. The difference is the estimated tax on the increase.',
    'The tax estimate is intentionally simple: it ignores UIF, retirement deductions, medical credits and year-to-date payroll effects, so treat the net figure as indicative.',
  ],
  formulas: [
    {
      label: 'New annual gross',
      expression: 'current annual gross × (1 + increase %)',
    },
    {
      label: 'Annual bonus',
      expression: 'bonus amount, or current annual gross × bonus %',
    },
    {
      label: 'Estimated tax on increase',
      expression: 'annual tax(new income) − annual tax(current income)',
      notes: `Uses the SARS brackets and the primary rebate of R${rebates2027.primary.toLocaleString('en-ZA')} only.`,
    },
    {
      label: 'Estimated net annual increase',
      expression: 'gross increase + bonus − estimated tax',
    },
  ],
  example: {
    title: 'A 6% increase on a R35 000 monthly salary',
    description:
      'An employee earning a monthly gross salary is offered a percentage increase plus a once-off bonus, and wants to see the net effect after tax.',
    inputs: {
      currentSalary: 35_000,
      salaryPeriod: 'monthly',
      increasePercent: 6,
      bonusValue: 10_000,
      bonusType: 'amount',
      includeTaxEstimate: true,
      annualGrossForTax: 420_000,
    },
  },
  definitions: [
    {
      term: 'Gross salary',
      description: 'Your salary before PAYE, UIF and any deductions are subtracted.',
    },
    {
      term: 'Net pay',
      description:
        'Take-home pay: what remains after tax and deductions. The optional tax estimate approximates this for the increase only.',
    },
    {
      term: 'Marginal tax rate',
      description:
        'The rate applied to the last Rand you earn. An increase is taxed at your marginal rate, which is why the net gain is less than the gross gain.',
    },
    {
      term: 'Bonus',
      description:
        'A once-off payment such as a 13th cheque. It is taxed as income and can push part of your earnings into a higher bracket.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'Why is the net estimate labelled optional?',
    answer:
      'Your actual tax depends on your full year-to-date income, deductions, benefits and employer payroll setup. We only estimate the marginal tax using the annual gross you provide.',
  },
  {
    question: 'How do I calculate a salary increase percentage?',
    answer:
      'Divide the increase amount by your current salary and multiply by 100. This calculator does it for you and shows the new gross and estimated net figures.',
  },
  {
    question: 'Is a bonus taxed differently from salary?',
    answer:
      'No — a bonus is taxed as normal income, but because it is paid once off, payroll may withhold tax at a higher rate for that month. The annual estimate here smooths that out.',
  },
  {
    question: 'Does the calculator include UIF or pension deductions?',
    answer:
      'No. The optional estimate applies the tax brackets and primary rebate only. For a full take-home calculation, use the salary calculator.',
  },
]
